import { TDatasetQueryParams } from '@infotech-soe/merps/types/Queries';
import {
  CategoryName,
  ConditionSetItemsP,
  ConditionSetName,
  ConditionSetP,
  DisplayModeName,
  IConditionDatasetParam,
  IDisplayModeCondInParam,
  IDisplayModeInParamParsed,
  IDisplayModeParsed,
  IInParamsValues,
  TDatasetConditionsValues,
  TDatasetName,
  tg,
} from '@infotech-soe/metaschema';
import pick from 'lodash/pick';

import { DatasetParamValues } from '../../infrastructure/providers/abstract/IApiProvider';
import CachedSchemaRepository from '../../infrastructure/schemas/storage/CachedSchemaRepository';
import ISchemaRepository from '../../infrastructure/schemas/storage/ISchemaRepository';
import { FormControlViewModelType } from '../form/view-model/FormControlViewModel';
import { FormControls } from '../form/view-model/FormViewModel';
import { DisplayModeConditions } from '../user-config/IUserConfigService';

import ICategoryConditionsService from './ICategoryConditionsService';

class CategoryConditionsServiceSingleton implements ICategoryConditionsService {
  private readonly schemaRepository: ISchemaRepository;
  constructor() {
    this.schemaRepository = new CachedSchemaRepository();
  }

  public async extractFromInParams(
    category: CategoryName,
    datasetName: TDatasetName,
    { inParams, datasets }: IDisplayModeParsed,
    inParamsValues?: IInParamsValues
  ): Promise<TDatasetConditionsValues> {
    if (!inParamsValues || !inParams || !datasets[datasetName].conditions) {
      return {};
    }
    const conditionsSet = await this.getConditionsSet(
      category,
      datasets[datasetName].conditions?.name ?? ''
    );
    const conditionParams: Array<[string, IDisplayModeCondInParam]> = [];
    for (const paramName of Object.keys(inParams)) {
      const definition: IDisplayModeInParamParsed = inParams[paramName];
      if (
        definition?.kind === 'condition' &&
        definition.cond in conditionsSet.items &&
        inParamsValues[paramName] !== undefined
      ) {
        conditionParams.push([paramName, definition]);
      }
    }

    return conditionParams.length
      ? Object.fromEntries(
          conditionParams.map(([name, def]) => [def.cond, inParamsValues[name]])
        )
      : {};
  }

  public async mergeConditions(
    category: CategoryName,
    displayMode: DisplayModeName,
    displayModeConditions: DisplayModeConditions,
    inParams?: IInParamsValues
  ): Promise<DisplayModeConditions> {
    const displayModeSchema = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    const result: DisplayModeConditions = {};
    for (const [datasetName, { conditions, source }] of Object.entries(
      displayModeSchema.datasets
    )) {
      const { conditions: datasetConditions, ...rest } = displayModeConditions[
        datasetName
      ]
        ? displayModeConditions[datasetName]
        : this.defaultCondition();
      if (conditions) {
        const { Base: datasetCategory } =
          await this.schemaRepository.getView(source);
        const extractedConditions = await this.extractFromInParams(
          datasetCategory,
          datasetName,
          displayModeSchema,
          inParams
        );

        const conditionsList = Object.keys(
          (await this.getConditionsSet(datasetCategory, conditions.name)).items
        );

        const storedConditions = Object.keys(datasetConditions).reduce(
          (a: TDatasetConditionsValues, e) => {
            if (conditionsList.includes(e)) {
              a[e] = datasetConditions[e];
            }
            return a;
          },
          {}
        );
        result[datasetName] = {
          conditions: this.sanitizeConditions({
            ...storedConditions,
            ...extractedConditions,
          }),
          ...rest,
        };
      } else {
        result[datasetName] = {
          conditions: {},
          ...rest,
        };
      }
    }
    return result;
  }

  private async getConditionsSet(
    category: CategoryName,
    cond: ConditionSetName
  ): Promise<ConditionSetP> {
    const categorySchema = await this.schemaRepository.getCategory(category);
    return categorySchema[cond] as ConditionSetP;
  }

  public async mergeTreeParams(
    category: CategoryName,
    displayMode: DisplayModeName,
    datasetName: TDatasetName,
    params: DatasetParamValues,
    conditions?: TDatasetConditionsValues,
    inParams?: IInParamsValues
  ): Promise<DatasetParamValues> {
    const { tree } = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    const treeParamValues: Record<string, any> = {};
    if (tree && 'params' in tree && tree.params) {
      for (const [paramName, treeParam] of Object.entries(tree.params)) {
        if ('condition' in treeParam) {
          const [conditionDataset, conditionName] =
            treeParam.condition.split('.');
          if (datasetName === conditionDataset) {
            treeParamValues[paramName] = conditions?.[conditionName];
          }
        } else if ('param' in treeParam && inParams) {
          treeParamValues[paramName] = inParams[treeParam.param];
        }
      }
    }
    return { ...params, ...treeParamValues };
  }

  public async extractTreeParams(
    category: CategoryName,
    displayMode: DisplayModeName,
    conditions: TDatasetConditionsValues,
    inParams?: IInParamsValues
  ): Promise<DatasetParamValues | undefined> {
    const { tree } = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    if (tree && 'params' in tree && tree.params) {
      const treeParamValues: Record<string, any> = {};
      for (const [paramName, treeParam] of Object.entries(tree.params)) {
        if ('condition' in treeParam) {
          const [conditionDataset, conditionName] = (
            treeParam as IConditionDatasetParam
          ).condition.split('.');
          treeParamValues[paramName] =
            conditions[conditionDataset].conditions[conditionName];
        } else if ('param' in treeParam && inParams) {
          treeParamValues[paramName] = inParams[treeParam.param];
        }
      }
      return treeParamValues;
    }
    return {};
  }

  public sanitizeConditions(
    dirtyConditions: TDatasetConditionsValues = {}
  ): TDatasetConditionsValues {
    const sanitizedConditions: Record<string, any> = {};
    Object.entries(dirtyConditions).forEach(([condName, cond]) => {
      if (!tg.isNothing(cond) && cond !== '') {
        sanitizedConditions[condName] = cond;
      }
    });
    return sanitizedConditions;
  }

  public defaultCondition = (): TDatasetQueryParams => ({
    queryOptions: {
      caseSensitive: false,
    },
    conditions: {},
    fieldsConditions: {},
  });

  public sanitizeConditionArgs(
    args: TDatasetConditionsValues,
    conditionSetItems: ConditionSetItemsP,
    controls: FormControls
  ): TDatasetConditionsValues {
    return pick(
      this.sanitizeConditions(args),
      Object.keys(conditionSetItems).filter(
        c =>
          !controls[c] ||
          (controls[c] &&
            !(
              controls[c].type === FormControlViewModelType.CHECKBOX &&
              args[c] === 0
            ))
      )
    );
  }

  public async hasConditionInParams(
    category: CategoryName,
    displayMode: DisplayModeName,
    inParamsValues: IInParamsValues | undefined
  ): Promise<boolean> {
    if (!inParamsValues) {
      return false;
    }
    const { inParams } = await this.schemaRepository.getDisplayMode(
      category,
      displayMode
    );
    if (!inParams) {
      return false;
    }
    return Object.entries(inParams).some(
      ([paramName, definition]) =>
        definition?.kind === 'condition' &&
        !tg.isNothing(inParamsValues[paramName]) &&
        !tg.isEmptyString(inParamsValues[paramName])
    );
  }
}

const CategoryConditionsService = (() => {
  let instance: ICategoryConditionsService;
  return new Proxy(CategoryConditionsServiceSingleton, {
    construct(
      Target: typeof CategoryConditionsServiceSingleton
    ): ICategoryConditionsService {
      if (!instance) {
        instance = new Target();
      }
      return instance;
    },
  });
})();

export default CategoryConditionsService;
